import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { MIN_PASSWORD } from "../ChangePasswordDialog";
import { PRODUCT_NAME } from "../club";
import { useApi } from "../services";
import { useDocumentTitle } from "../useDocumentTitle";
import { useT } from "../../i18n/I18nProvider";
import { Button } from "../../ui/Button";
import { TextField } from "../../ui/Field";
import { useFormErrors, type FormErrors } from "../../ui/form";

/** Public page: sets a new password with the one-time code an administrator handed over. */
export function ResetPasswordPage() {
  const t = useT();
  const api = useApi();
  const toErrors = useFormErrors();
  const [params] = useSearchParams();
  const [token, setToken] = useState((params.get("token") ?? "").trim());
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [errors, setErrors] = useState<FormErrors>({ fields: {} });
  const [done, setDone] = useState(false);
  const redeem = useMutation({ mutationFn: api.auth.redeemPasswordReset });
  useDocumentTitle(t("reset.title"));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const fields: Record<string, string> = {};
    if (!token.trim()) fields.token = t("validation.required");
    if (next.length < MIN_PASSWORD) fields.new_password = t("account.passwordShort", { count: MIN_PASSWORD });
    else if (repeat !== next) fields.repeat = t("account.passwordMismatch");
    setErrors({ fields });
    if (Object.keys(fields).length) return;
    try {
      await redeem.mutateAsync({ token: token.trim(), new_password: next });
      setDone(true);
    } catch (error) {
      setErrors(toErrors(error, { codeFields: { invalid_reset_token: "token" } }));
    }
  }

  return (
    <div className="cc-center">
      <main className="cc-auth-card">
        <div className="cc-wordmark">{PRODUCT_NAME}</div>
        <h1>{t("reset.title")}</h1>
        {done ? (
          <>
            <p className="cc-tagline" role="status">{t("reset.done")}</p>
            <Link className="cc-button cc-button--primary" to="/sign-in">{t("reset.toSignIn")}</Link>
          </>
        ) : (
          <>
            <p className="cc-tagline">{t("reset.body")}</p>
            <form className="cc-form" onSubmit={(event) => void submit(event)} noValidate>
              {errors.form ? <p className="cc-alert" role="alert">{errors.form}</p> : null}
              <TextField label={t("reset.token")} value={token} onChange={(event) => setToken(event.target.value)} error={errors.fields.token} required autoComplete="off" spellCheck={false} autoCapitalize="none" />
              <TextField label={t("account.newPassword")} type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} error={errors.fields.new_password} hint={t("account.passwordHint", { count: MIN_PASSWORD })} required />
              <TextField label={t("account.confirmPassword")} type="password" autoComplete="new-password" value={repeat} onChange={(event) => setRepeat(event.target.value)} error={errors.fields.repeat} required />
              <Button type="submit" variant="primary" busy={redeem.isPending}>{t("reset.submit")}</Button>
            </form>
            <Link to="/sign-in" className="cc-back">{t("reset.backToSignIn")}</Link>
          </>
        )}
      </main>
    </div>
  );
}
